import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { localizeGalleryItem, type GalleryItem } from '../data/galleryData';

interface GalleryDetailModalProps {
  item: GalleryItem;
  onClose: () => void;
  onLike: () => void;
  onUsePrompt: (prompt: string) => void;
}

export default function GalleryDetailModal({ item, onClose, onLike, onUsePrompt }: GalleryDetailModalProps) {
  const { t, i18n } = useTranslation();
  const displayItem = localizeGalleryItem(item, i18n.language);
  const navigate = useNavigate();
  const [imgLoaded, setImgLoaded] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleGenerateSimilar = () => {
    onClose();
    navigate(`/generate?prompt=${encodeURIComponent(displayItem.prompt)}`);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal content */}
      <div
        className="relative bg-white w-full max-w-md mx-auto sm:max-w-lg sm:rounded-3xl rounded-t-3xl max-h-[92vh] overflow-hidden flex flex-col"
        style={{
          animation: 'slideUp 0.3s ease-out',
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center cursor-pointer active:scale-90 transition-transform"
        >
          <i className="ri-close-line text-lg"></i>
        </button>

        {/* Image */}
        <div className="relative w-full flex-shrink-0 bg-gray-100" style={{ maxHeight: '60vh' }}>
          <img
            src={displayItem.imageUrl}
            alt={displayItem.prompt}
            className={`w-full object-cover object-top transition-opacity duration-500 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
            style={{ maxHeight: '60vh' }}
            onLoad={() => setImgLoaded(true)}
          />
          {!imgLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-pink-300 border-t-pink-500 rounded-full animate-spin" />
            </div>
          )}

          {/* Tool badge */}
          <div className="absolute top-3 left-3">
            <span
              className="px-2.5 py-1 rounded-full text-xs font-medium text-white"
              style={{
                background: 'rgba(0,0,0,0.5)',
                backdropFilter: 'blur(8px)',
              }}
            >
              {displayItem.styleName}
            </span>
          </div>
        </div>

        {/* Info */}
        <div className="flex-1 overflow-y-auto ios-scroll p-5">
          {/* Author */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full overflow-hidden border border-gray-100">
                <img src={displayItem.authorAvatar} alt={displayItem.authorName} className="w-full h-full object-cover object-top" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">{displayItem.authorName}</p>
                <p className="text-xs text-gray-400">{displayItem.createdAt}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <i className="ri-eye-line text-gray-400 text-sm"></i>
                <span className="text-xs text-gray-500">{displayItem.views}</span>
              </div>
              <button
                onClick={onLike}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full cursor-pointer active:scale-90 transition-all"
                style={displayItem.liked ? { background: '#FF6B9D18' } : { background: 'rgba(0,0,0,0.04)' }}
              >
                <i className={`${displayItem.liked ? 'ri-heart-fill text-pink-500' : 'ri-heart-line text-gray-500'} text-sm`}></i>
                <span className={`text-xs font-medium ${displayItem.liked ? 'text-pink-500' : 'text-gray-600'}`}>{displayItem.likes}</span>
              </button>
            </div>
          </div>

          {/* Prompt */}
          <div
            className="rounded-2xl p-4 mb-4"
            style={{ background: 'rgba(255,107,157,0.06)', border: '1px solid rgba(255,107,157,0.1)' }}
          >
            <div className="flex items-center gap-1.5 mb-2">
              <i className="ri-quill-pen-ai-line text-pink-400 text-xs"></i>
              <span className="text-xs text-gray-400">{t('gen_promptLabel')}</span>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">{displayItem.prompt}</p>
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => onUsePrompt(displayItem.prompt)}
              className="py-3.5 rounded-xl text-white font-semibold text-sm cursor-pointer active:scale-95 transition-all whitespace-nowrap"
              style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}
            >
              <i className="ri-magic-line mr-1"></i>
              {t('gallery_usePrompt')}
            </button>
            <button
              onClick={handleGenerateSimilar}
              className="py-3.5 rounded-xl font-semibold text-sm cursor-pointer active:scale-95 transition-all whitespace-nowrap"
              style={{ background: 'rgba(255,107,157,0.08)', color: '#FF6B9D', border: '1px solid rgba(255,107,157,0.15)' }}
            >
              <i className="ri-sparkling-line mr-1"></i>
              {t('gallery_similarCreate')}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
